import { SchedulerType } from '@common/queries/job';
import { TextDocument, TextDocuments } from '@common/types/messageTypes';
import {
  CreateGem,
  MetadataDidReload,
  TextDocumentDidCreate,
  TextDocumentDidDelete,
  TextDocumentDidMove,
  TextDocumentDidSwitch
} from '@LSP/base/hook';
import { PipelineLSPClient } from '@LSP/pipeline/client';
import { CodeViewMode, FileDefinitions, ViewType } from '@LSP/types';
import { PipelineInitializeParams } from '@pages/Workflow/types';
import { BackEndCodeLanguageType } from '@prophecy/graphql-types/enums';
import { ChangeDescriptor, FileDescriptor } from '@prophecy/ui-v3/Editor/types';
import { createContext as createZustandContext } from '@prophecy/utils/react/useZustandContext';
import { noop } from 'lodash-es';
import { createContext } from 'react';

import { WorkflowInterimMode } from '../../constants/workflow';
import { FunctionType } from '../../redux/types';
import { GemRecord } from '../gems/types';
import { ProjectWiseFunctions } from '../ProjectBrowser/Functions/types';
import { SearchableGraphNodeItem } from '../ProjectBrowser/types';
import { SparkSourceSuggestion, SourceSuggestionsReferenceProcess, CommonState, CommonGraph } from '../types';
import { Entity, EntityToCreate, IDEEntity } from '../types/Entity';
import { AddProcessType } from '../utils/graph/process';

// oxlint-disable-next-line import/default
import type MonacoEditor from 'monaco-editor/esm/vs/editor/editor.api';

export type FilesMap = { [path: string]: FileDescriptor };

export type SelectedFileType = { entityId: string; filePath: string; selectedEntity: Entity };

export type IdeFileMapType = { [key: string]: { files: FilesMap; loading: boolean; entity: Entity; name?: string } };

export enum FooterCardKeys {
  diagnostics = 'diagnostics',
  metrics = 'metrics',
  code = 'code'
}
export type SelectedFooterCardState = {
  key: FooterCardKeys;
  currentTab?: string;
  activeLogIndex?: number;
  metadata?: Record<string, unknown>;
};
export type FocusNodeInfo = { nodePath: string; zoom: number; duration: number; selected?: boolean };
export enum GemStep {
  query = 'query',
  spec = 'spec'
}
export type GemState = {
  showGemPreview: boolean;
  activeStep?: GemStep;
  showMissingQueryFile?: boolean;
  showMissingSpecFile?: boolean;
};

export type PipelineStaleMapType = {
  pyFile: string;
  pipelinePyFile: string;
  stale?: boolean;
  config?: { projectConfig?: string; pipelineConfig?: string };
  modelFiles: { [key: string]: string };
  runtime?: number;
};

export type PipelineAppMapType = {
  [key: string]: {
    closedApps?: string[];
    totalOpenedApps?: string[];
    overflowItems?: string[];
    appMap?: PipelineStaleMapType;
  };
};

export type IdeState = {
  ide: {
    uid: string;
    runAppOnceMap: { [appName: string]: boolean };
    entity: IDEEntity;
    isIDELoading?: boolean;
    pipelineAppsMap: PipelineAppMapType;
    repositoryPath?: string;
    branch?: string;
    version?: string;
    viewType: ViewType;
    activeDialogType?: string;
    defaultExpandAll: boolean;
    files: IdeFileMapType;
    selectedFiles: SelectedFileType[];
    codeViewMode: CodeViewMode;
    path?: string;
    isReadOnly?: boolean;
    monitoringSelectedFiles: { [key: string]: SelectedFileType | undefined };
    entityTypeToCreate?: EntityToCreate;
    needsMetadataReload?: boolean;
    selectedFooterCard?: SelectedFooterCardState;
    searchQuery?: string;
    selectedGem?: {
      gemKind?: string;
      gemCategory?: string;
    };
    gemList?: GemRecord[];
    functions?: ProjectWiseFunctions;
    sparkSourceSuggestions?: SparkSourceSuggestion[];
    gemIde: GemState;
    disabledServerlessFormats: string[];
    isRemapping?: boolean;
    fabricId?: string;
  };
  graph: {
    nodeToFocus?: FocusNodeInfo;
    graphPath?: string;
    selectedNodeIds?: string[];
    interimMode?: WorkflowInterimMode;
    interimModeEnabled?: boolean;
  };
  project: {
    id: string;
    name: string;
    language: BackEndCodeLanguageType;
  };
  projectBrowserWidth: number;
  chatPanelWidth: number;
  hasProjectBrowser: boolean;
  hasChatPanel: boolean;
  footerDrawerHeight: number;
  footerDrawerOpen: boolean;
  pipelineLspClient?: PipelineLSPClient<PipelineInitializeParams>;
};
export type ChangeViewType = (viewType: ViewType, cleanFiles?: boolean) => void;
export type TextDocumentsWithDiagnostic = Pick<TextDocument, 'uri' | 'diagnostics'>[];
const { Context: IdeStateContext, Provider: IdeStateZustandProvider } = createZustandContext<IdeState | null>(null);
export { IdeStateContext, IdeStateZustandProvider };
export type IdeActions = {
  addPipelineToJobIDE: (pipelineName: string) => void;
  setJobViewType: (viewType: ViewType) => void;
  setAppPipelineStaleMap: (pipelineAppMap: PipelineAppMapType) => void;
  schedulerType?: SchedulerType;
  jobMetadataDidReload: () => void;
  setHoveredSuggestion: React.Dispatch<
    React.SetStateAction<
      | {
          source: SparkSourceSuggestion;
          process: SourceSuggestionsReferenceProcess;
        }
      | undefined
    >
  >;
  toggleRunAppOnce: (entityName: string, isRunApp: boolean) => void;
  setPipelineAppMap: (
    pipelineName: string,
    closedApps: string[],
    totalOpenedApps: string[],
    overflowItems: string[]
  ) => void;
  sourceSuggestionClickHandler: () => void;
  setSourceSuggestions: (sparkSourceSuggestions?: SparkSourceSuggestion[]) => void;
  refreshDependencies: (isProject?: boolean) => void;
  setProjectBrowserWidth: (width: number, show: boolean) => void;
  setChatPanelWidth: (width: number, show: boolean, persist?: boolean) => void;
  setFooterDrawerHeight: (height: number) => void;
  setFooterDrawerOpen: (open: boolean) => void;
  setReadOnly: (isReadOnly: boolean) => void;
  setInterimModeEnabled: (interimModeEnabled: boolean) => void;
  setInterimMode: (interimMode: WorkflowInterimMode) => void;
  setEntity: (p: {
    uid: string;
    entity: IDEEntity;
    branch?: string;
    version?: string;
    repositoryPath?: string;
  }) => void;
  toggleActiveDialog: (value?: string) => void;
  reinitializeLSPconnection: () => void;
  setFiles: (params: SetFilesParams) => void;
  updateFiles: (params: UpdateFilesParams) => void;
  setSelectedFile: (file: string, entityId: string) => void;
  changeViewType: ChangeViewType;
  setMonitoringSelectedFile: (file: string, entityId: string, entity: Entity) => void;
  changeFile: (changes: ChangeDescriptor[], entityId: string) => void;
  changeFileMetadata: (fileDefinitions: FileDefinitions, entityId: string) => void;
  filesDiagnostics: (files: TextDocumentsWithDiagnostic, entityId: string) => void;
  toggleFileLoader: (entityId: string) => void;
  addProcess: AddProcessType;
  metadataDidReload: MetadataDidReload;
  textDocumentDidCreate: TextDocumentDidCreate;
  textDocumentDidDelete: TextDocumentDidDelete;
  textDocumentDidMove: TextDocumentDidMove;
  textDocumentDidSwitch: (uri: string, position?: MonacoEditor.IPosition) => ReturnType<TextDocumentDidSwitch>;
  createGem: CreateGem;
  setEntityTypeToCreate: (entityTypeToCreate: EntityToCreate) => void;
  markToReloadMetadata: (needsMetadataReload: boolean) => void;
  setSelectedFooterCard: (selectedFooterCard?: SelectedFooterCardState) => void;
  openDialogRuntimeLogs?: () => void;
  uploadJobResource?: () => void;
  setSearchQuery: (query: string) => void;
  setSelectedGem: (gemKind?: string, gemCategory?: string, debounced?: boolean) => void;
  focusNodeInGraph: (nodeToFocus?: FocusNodeInfo) => void;
  setGraphPath: (graphPath?: string) => void;
  changeGraphPath: (graphPath: string) => void;
  setSelectedNodeIds: (nodeIds: string[]) => void;
  setGemList: (gemList: GemRecord[]) => void;
  searchNodes: (searchQuery: string) => SearchableGraphNodeItem[];
  setFunctions: (functions: ProjectWiseFunctions) => void;
  setGemState: (state: Partial<GemState>) => void;
  addFunction: (
    params: { name: string; type: FunctionType; propertyPath: string; description?: string },
    save: boolean
  ) => void;
  removeFunction: (propertyPath: string, save: boolean) => void;
  setActiveLogIndex: (index: number | undefined) => void;
  openSearch: () => void;
  getEntityRootGraph: <T extends CommonGraph = CommonGraph>(state: CommonState) => { graph?: T; graphPath?: string };
  setPipelineLspClient: (lspClient?: PipelineLSPClient<PipelineInitializeParams>) => void;
  setDisabledServerlessFormats: (disabledServerlessFormats: string[]) => void;
  scrollToMessage: (messageId: string) => Promise<void>;
  setIsRemapping: (isRemapping: boolean) => void;
  setIsIDELoading: (isIDELoading: boolean) => void;
  setFabricId: (fabricId?: string) => void;
};
export type ActionContextType = {
  actions: React.MutableRefObject<IdeActions>;
  setActions: React.Dispatch<React.SetStateAction<IdeActions>>;
};

export function defaultGetEntityRootGraph<T extends CommonGraph = CommonGraph>(
  state: CommonState
): {
  graph?: T;
  graphPath?: string;
} {
  return { graph: state.currentGraph as T, graphPath: state.root };
}

export const defaultGetGemSpecs = (state: CommonState) => state.gemSpecs;

export const IdeActionsContext = createContext<ActionContextType>({
  actions: {
    current: {
      setGemState: noop,
      setAppPipelineStaleMap: noop,
      toggleRunAppOnce: noop,
      setHoveredSuggestion: noop,
      sourceSuggestionClickHandler: noop,
      setSourceSuggestions: noop,
      setProjectBrowserWidth: noop,
      setChatPanelWidth: noop,
      setFooterDrawerHeight: noop,
      setFooterDrawerOpen: noop,
      setReadOnly: noop,
      setEntity: noop,
      setInterimModeEnabled: noop,
      setInterimMode: noop,
      setPipelineAppMap: noop,
      toggleActiveDialog: noop,
      setFiles: noop,
      refreshDependencies: noop,
      updateFiles: noop,
      setSelectedFile: noop,
      changeViewType: noop,
      reinitializeLSPconnection: noop,
      setMonitoringSelectedFile: noop,
      changeFile: noop,
      changeFileMetadata: noop,
      filesDiagnostics: noop,
      toggleFileLoader: noop,
      addProcess: noop as AddProcessType<CommonGraph>,
      metadataDidReload: noop as MetadataDidReload,
      textDocumentDidCreate: noop as TextDocumentDidCreate,
      textDocumentDidDelete: noop as TextDocumentDidDelete,
      textDocumentDidMove: noop as TextDocumentDidMove,
      textDocumentDidSwitch: noop as TextDocumentDidSwitch,
      createGem: noop as CreateGem,
      setEntityTypeToCreate: noop,
      markToReloadMetadata: noop,
      setSelectedFooterCard: noop,
      setSearchQuery: noop,
      setSelectedGem: noop,
      focusNodeInGraph: noop,
      changeGraphPath: noop,
      setGraphPath: noop,
      setSelectedNodeIds: noop,
      setGemList: noop,
      setFunctions: noop,
      addFunction: noop,
      removeFunction: noop,
      setActiveLogIndex: noop,
      searchNodes: () => [],
      openSearch: noop,
      getEntityRootGraph: defaultGetEntityRootGraph,
      setPipelineLspClient: noop,
      setDisabledServerlessFormats: noop,
      scrollToMessage: () => Promise.resolve(),
      addPipelineToJobIDE: noop,
      setJobViewType: noop,
      setIsRemapping: noop,
      schedulerType: undefined,
      jobMetadataDidReload: noop,
      setIsIDELoading: noop,
      setFabricId: noop
    }
  },
  setActions: noop
});
export type SetFilesParams = {
  textDocuments: TextDocuments;
  uid: string;
  entity: Entity;
  name?: string;
  loadFiles?: boolean;
  primaryFilePath?: string;
};
export type UpdateFilesParams = {
  textDocuments: TextDocuments;
  uid: string;
};
